import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { SortDirection } from "@bridgesplit/utils";
import Decimal from "decimal.js";

import { MarketInformationAssetData } from "./domain";
import { Strategy, StrategyInfo, StrategiesAggregate } from "./domain/strategy";
import { Replace } from "../reducers/type-util";

export interface LendingStrategyFilter {
    userAddress?: string;
    principalMints?: string[];
    addresses?: string[];
    showArchived?: boolean;
    /** Summary endpoint only. When non-empty, the response's `terms.assetTerms` is
     * populated with entries for these collateral mints (plus their durations,
     * APYs, and allocation info). Empty/omitted = no terms returned (default
     * summary endpoint behavior). Ignored by the full `/strategy/infos` endpoint. */
    collateralTermsAssetFilter?: string[];
}

export interface LendingStrategyPaginationParams {
    sortType: StrategySortType;
    sortDirection: SortDirection;
    page: number;
    pageSize: number;
}

export type LendingStrategyPagination = LendingStrategyPaginationParams & LendingStrategyFilter;

export interface UserStrategiesFilter {
    userAddress?: string;
    principalMints?: string[];
    showArchived?: boolean;
    sortDirection: SortDirection;
    page: number;
    pageSize: number;
}

export interface UserStrategyGroup {
    principalMint: string;
    principalMetadata: TokenListMetadata;
    aggregate: StrategiesAggregate;
    /** Sum of `principal_deployed` across the group, in `principalMint`'s
     *  native base units (decimals not applied). */
    principalDeployedNative: number;
    /** Idle principal (token-account balance + external yield balance).
     *  Native base units. */
    strategyBalanceNative: number;
    /** `principalDeployedNative + strategyBalanceNative`. Native base units. */
    totalSupplyNative: number;
}

export enum StrategySortType {
    NetAssetValue,
    RecentlyCreated
}

export type StrategyExpanded = Replace<
    Strategy,
    {
        interestPerSecond: Decimal;
        fixedApy: number; //  should be Decimal
        wAvgApy: number; // should be Decimal
    }
>;

export type StrategyExpandedInfo = Replace<
    StrategyInfo,
    {
        strategy: StrategyExpanded;
        totalBalance: number; // is amount available to withdraw
        principalMetadata: TokenListMetadata;
        collateral: StrategyCollateralExpanded[];
    }
>;

export interface StrategyCollateralExpanded {
    metadata: TokenListMetadata;
    durationAndApys: Record<string, number>;
    ltvInfo: LtvInfo;
}

//API return type
export interface LtvInfo {
    ltv: number;
    liquidationThreshold: number;
}

export type CollateralWithTerms = {
    metadata: TokenListMetadata;
    //  all LTV infos if viewing across mutliple strateg
} & (
    | { allLtvInfos: MarketInformationAssetData[] }
    // single LTV info if strategy already exists
    | { ltvInfo: LtvInfo }
);

export enum OriginationCapStatus {
    Accepting = "Accepting",
    Closed = "Closed"
}

export const STRATEGY_TEMPLATE_ALL_IDENTIFIER = "all";

export enum CapType {
    Supply,
    Borrow,
    Withdraw
}
